import React, { useCallback, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function extractTableData(tableEl: HTMLTableElement): { headers: string[]; rows: string[][] } {
  const headers: string[] = [];
  const rows: string[][] = [];

  const ths = tableEl.querySelectorAll('thead th');
  ths.forEach(th => headers.push((th.textContent || '').trim()));

  const trs = tableEl.querySelectorAll('tbody tr');
  trs.forEach(tr => {
    const row: string[] = [];
    tr.querySelectorAll('td').forEach(td => row.push((td.textContent || '').trim()));
    rows.push(row);
  });

  // Fallback: if no thead, use first row as header
  if (headers.length === 0 && rows.length > 0) {
    const firstTr = tableEl.querySelector('tr');
    if (firstTr) {
      const cells = firstTr.querySelectorAll('th, td');
      cells.forEach(c => headers.push((c.textContent || '').trim()));
    }
  }

  return { headers, rows };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadCSV(headers: string[], rows: string[][]) {
  const escape = (s: string) =>
    s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;

  const lines = [
    headers.map(escape).join(','),
    ...rows.map(r => r.map(escape).join(',')),
  ].join('\n');

  const bom = '\uFEFF';
  const blob = new Blob([bom + lines], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, 'tabela.csv');
}

function downloadXLSX(headers: string[], rows: string[][]) {
  const data = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Dados');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  downloadBlob(blob, 'tabela.xlsx');
}

const STYLE_PROPS = [
  'background-color', 'color', 'font-family', 'font-size', 'font-weight',
  'text-align', 'padding', 'border', 'border-collapse', 'border-spacing',
  'border-top', 'border-right', 'border-bottom', 'border-left',
  'line-height', 'white-space', 'vertical-align',
] as const;

function copyComputedStyles(src: Element, dst: Element) {
  const computed = window.getComputedStyle(src);
  const dstEl = dst as HTMLElement;
  for (const prop of STYLE_PROPS) {
    dstEl.style.setProperty(prop, computed.getPropertyValue(prop));
  }
  for (let i = 0; i < src.children.length; i++) {
    if (dst.children[i]) copyComputedStyles(src.children[i], dst.children[i]);
  }
}

function downloadPNG(tableEl: HTMLTableElement) {
  try {
    const clone = tableEl.cloneNode(true) as HTMLTableElement;
    copyComputedStyles(tableEl, clone);

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;left:-9999px;top:0;';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    const w = clone.offsetWidth;
    const h = clone.offsetHeight;
    const pad = 16;

    const serializer = new XMLSerializer();
    const div = document.createElement('div');
    div.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    div.style.cssText = `padding:${pad}px;background:#fff;`;
    div.appendChild(clone.cloneNode(true));
    const htmlStr = serializer.serializeToString(div);

    document.body.removeChild(wrapper);

    const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="${w + pad * 2}" height="${h + pad * 2}">
      <foreignObject width="100%" height="100%">${htmlStr}</foreignObject>
    </svg>`;

    // Use data URL instead of blob URL to avoid tainted canvas
    const img = new Image();
    const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);

    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = (w + pad * 2) * scale;
      canvas.height = (h + pad * 2) * scale;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        if (blob) downloadBlob(blob, 'tabela.png');
      }, 'image/png');
    };
    img.onerror = () => {
      console.error('Failed to render table SVG to image');
    };
    img.src = svgDataUrl;
  } catch (e) {
    console.error('Failed to download table as PNG:', e);
  }
}

const TableBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);

  const handleDownload = useCallback((format: 'csv' | 'xlsx' | 'png') => {
    const tableEl = tableRef.current?.querySelector('table');
    if (!tableEl) return;
    if (format === 'png') {
      downloadPNG(tableEl);
      setShowMenu(false);
      return;
    }
    const { headers, rows } = extractTableData(tableEl);
    if (headers.length === 0 && rows.length === 0) return;
    if (format === 'csv') downloadCSV(headers, rows);
    else downloadXLSX(headers, rows);
    setShowMenu(false);
  }, []);

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  return (
    <TableWrapper>
      <TableToolbar>
        <DownloadWrapper ref={menuRef}>
          <ToolbarButton
            title="Baixar tabela"
            onClick={() => setShowMenu(prev => !prev)}
          >
            <DownloadIcon />
          </ToolbarButton>
          {showMenu && (
            <DropdownMenu>
              <DropdownItem onClick={() => handleDownload('png')}>PNG</DropdownItem>
              <DropdownItem onClick={() => handleDownload('xlsx')}>XLSX</DropdownItem>
              <DropdownItem onClick={() => handleDownload('csv')}>CSV</DropdownItem>
            </DropdownMenu>
          )}
        </DownloadWrapper>
      </TableToolbar>
      <div ref={tableRef}>
        {children}
      </div>
    </TableWrapper>
  );
};

export default TableBlock;

const TableWrapper = styled.div`
  position: relative;
  max-width: 100%;
  overflow-x: auto;
`;

const TableToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2px;
`;

const DownloadWrapper = styled.div`
  position: relative;
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.action_icon_color};
  padding: 4px;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.font_color};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.chart_bg};
  border: 1px solid ${({ theme }) => theme.chart_border};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 10;
  min-width: 80px;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.font_color};
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.chart_border};
  }
`;
